/**
 * Luxury mobile drawer controller — V5 native reset.
 * Horizon's header-drawer.js remains the single owner of opening/closing the drawer
 * and native menu drill-downs. This component only manages the two luxury tabs and
 * category menu sheets.
 */
class LuxuryMobileMenu extends HTMLElement {
  #details = null;
  #drawer = null;
  #activePanel = null;
  #activeTrigger = null;
  #closeTimer = null;

  connectedCallback() {
    this.#details = this.closest('details');
    this.#drawer = this.closest('.menu-drawer');

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeyDown);
    this.#details?.addEventListener('toggle', this.#onDrawerToggle);

    this.#setTab(this.dataset.defaultTab === 'categories' ? 'categories' : 'menu');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.#details?.removeEventListener('toggle', this.#onDrawerToggle);
    window.clearTimeout(this.#closeTimer);
  }

  #onDrawerToggle = () => {
    if (!this.#details?.open) {
      this.#closeCategory(false, true);
      this.#setTab(this.dataset.defaultTab === 'categories' ? 'categories' : 'menu');
    }
  };

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('[data-luxury-menu-tab]');
    if (tab instanceof HTMLButtonElement) {
      const name = tab.dataset.luxuryMenuTab;
      if (name === 'menu' || name === 'categories') this.#setTab(name);
      return;
    }

    const trigger = event.target.closest('[data-category-open]');
    if (trigger instanceof HTMLButtonElement) {
      const id = trigger.dataset.categoryOpen;
      if (id) this.#openCategory(id, trigger);
      return;
    }

    if (event.target.closest('[data-category-close]')) {
      event.preventDefault();
      this.#closeCategory(true);
    }
  };

  #onKeyDown = (event) => {
    if (event.key === 'Escape' && this.#activePanel) {
      event.preventDefault();
      event.stopPropagation();
      this.#closeCategory(true);
      return;
    }

    if (event.key !== 'Tab' || !this.#activePanel) return;
    const focusable = this.#getFocusable(this.#activePanel);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  #setTab(name) {
    this.#closeCategory(false, true);

    this.querySelectorAll('[data-luxury-menu-tab]').forEach((button) => {
      const active = button.dataset.luxuryMenuTab === name;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', active ? 'true' : 'false');
      button.setAttribute('tabindex', active ? '0' : '-1');
    });

    this.querySelectorAll('[data-luxury-tab-panel]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      const active = panel.dataset.luxuryTabPanel === name;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
      if (active) panel.scrollTop = 0;
    });

    this.dataset.activeTab = name;
  }

  #openCategory(id, trigger) {
    const panel = this.querySelector(`[data-category-panel="${CSS.escape(id)}"]`);
    if (!(panel instanceof HTMLElement)) return;

    window.clearTimeout(this.#closeTimer);
    this.#closeCategory(false, true);

    this.#activePanel = panel;
    this.#activeTrigger = trigger;
    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    panel.setAttribute('aria-hidden', 'false');
    this.classList.add('has-category-open');
    this.#drawer?.classList.add('has-luxury-category-open');

    requestAnimationFrame(() => {
      panel.classList.add('is-open');
      const close = panel.querySelector('.menu-drawer__category-close');
      if (close instanceof HTMLElement) close.focus({ preventScroll: true });
    });
  }

  #closeCategory(restoreFocus = true, immediate = false) {
    const panel = this.#activePanel;
    const trigger = this.#activeTrigger;
    if (!(panel instanceof HTMLElement)) return;

    window.clearTimeout(this.#closeTimer);
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    trigger?.setAttribute('aria-expanded', 'false');
    this.classList.remove('has-category-open');
    this.#drawer?.classList.remove('has-luxury-category-open');

    const finish = () => {
      panel.hidden = true;
      if (restoreFocus && trigger instanceof HTMLElement) trigger.focus({ preventScroll: true });
    };

    if (immediate) finish();
    else this.#closeTimer = window.setTimeout(finish, 280);

    this.#activePanel = null;
    this.#activeTrigger = null;
  }

  #getFocusable(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element instanceof HTMLElement && element.offsetParent !== null);
  }
}

if (!customElements.get('luxury-mobile-menu')) {
  customElements.define('luxury-mobile-menu', LuxuryMobileMenu);
}
